import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  ReagentItemInfo,
  ReagentOwnType,
  ReagentSpecInfo,
} from "@/types/specs";
import { ColumnDef } from "@tanstack/react-table";

import { getIcon, getPrice } from "../../utils";
import ChemicalSpecsAlternativeRadioGroup from "../ChemicalSpecsAlternativeRadioGroup";
import ManufacturerMultiSelect from "../ManufacturerMultiSelect";

interface ChemicalSpecColumnsProps {
  ownTypeFilter: string;
  setOwnTypeFilter: (value: string) => void;
  manufacturerFilter: string[];
  setManufacturerFilter: (value: string[]) => void;
  manufacturers: string[];
  hasEmptyManufacturers: boolean;
}

export function createChemicalSpecColumns({
  ownTypeFilter,
  setOwnTypeFilter,
  manufacturerFilter,
  setManufacturerFilter,
  manufacturers,
  hasEmptyManufacturers,
}: ChemicalSpecColumnsProps): ColumnDef<ReagentSpecInfo>[] {
  return [
    {
      id: "index",
      header: "",
      cell: ({ row }) => {
        return (
          <span className="flex justify-center text-sm text-gray-A font-normal">
            {row.index + 1}
          </span>
        );
      },
      size: 40,
    },
    {
      accessorKey: "item.item_name",
      header: "제품명",
      cell: ({ row }) => {
        const { item }: { item: ReagentItemInfo } = row.original;

        return (
          <span className="flex justify-start text-sm font-normal">
            {item.item_name}
          </span>
        );
      },
      size: 450,
    },
    {
      accessorKey: "item.item_no",
      header: "제품번호",
      cell: ({ row }) => {
        const { item }: { item: ReagentItemInfo } = row.original;
        return (
          <span className="flex justify-start text-sm font-normal">
            {item.item_no}
          </span>
        );
      },
      size: 111,
    },
    {
      accessorKey: "item.item_spec",
      header: "규격",
      cell: ({ row }) => {
        const { item }: { item: ReagentItemInfo } = row.original;
        return (
          <span className="flex justify-start text-sm font-normal">
            {item.spec}
          </span>
        );
      },
      size: 132,
    },
    {
      accessorKey: "manufacturer",
      header: () => {
        return (
          <div className="flex items-center justify-between w-full">
            <span>제조사</span>
            <ManufacturerMultiSelect
              manufacturers={manufacturers}
              selected={manufacturerFilter}
              onChange={setManufacturerFilter}
              hasEmptyManufacturers={hasEmptyManufacturers}
            />
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <span className="flex justify-start text-sm font-normal">
            {row.original.manufacturer}
          </span>
        );
      },
      size: 140,
    },
    {
      accessorKey: "quantity",
      header: "수량",
      cell: ({ row }) => {
        return (
          <span className="flex justify-start text-sm font-normal">
            {row.original.quantity}
          </span>
        );
      },
      size: 80,
    },
    {
      accessorKey: "unit",
      header: "단위",
      cell: ({ row }) => {
        return (
          <span className="flex justify-start text-sm font-normal">
            {row.original.unit}
          </span>
        );
      },
      size: 70,
    },
    {
      accessorKey: "own_type",
      header: () => {
        return (
          <div className="flex items-center justify-between w-full">
            <span>자사제품 여부</span>
            <Select value={ownTypeFilter} onValueChange={setOwnTypeFilter}>
              <SelectTrigger className="w-auto h-auto border-none bg-transparent p-1 hover:bg-white/10 rounded"></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">전체</SelectItem>
                <SelectItem value="in-house">자사제품</SelectItem>
                <SelectItem value="external">타사제품</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );
      },
      cell: ({ row }) => {
        const ownType = row.original.own_type;
        const altItems = row.original.alt_items;
        return (
          <div className="justify-items-center">
            {getIcon(ownType === ReagentOwnType.IN_HOUSE, altItems.length > 0)}
          </div>
        );
      },
      size: 110,
    },
    {
      header: "제품 정보",
      cell: ({ row }) => {
        const { own_type, item, alt_items, spec_id } = row.original;
        if (own_type === ReagentOwnType.IN_HOUSE) {
          return (
            <div className="text-sm font-normal">
              {`${item.item_no} / ${item.item_name} / ${getPrice(item?.list_price)}`}
            </div>
          );
        } else if (alt_items.length > 0) {
          return (
            <ChemicalSpecsAlternativeRadioGroup
              altItems={alt_items}
              spec_id={spec_id}
            />
          );
        } else {
          return <span>-</span>;
        }
      },
      size: 350,
    },
  ];
}
